import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Equalizer, Send } from "@material-ui/icons";

import { submitProgressReport } from "../../../../reducer/studentResearch/action";
import { notification } from "../../../../reducer/home/action";
import {
  PROGRESS_REPORT_STATUS_DRAFT,
  PROGRESS_REPORT_STATUS_SUBMITTED,
  PROGRESS_REPORT_STATUS_ACCEPTED,
  PROGRESS_REPORT_STATUS_ADJUST_REQUEST,
  PROGRESS_REPORT_STATUS_REJECTED,
  PROGRESS_REPORT_STATUS_LABEL,
  PROGRESS_REPORT_ITERATION,
} from "../../../../constants/projectStatus";
import SectionCard from "../../utils/SectionCard";
import useDueWindow from "../../utils/useDueWindow";

// ProgressForm — Refactor 2026-08-26: đề tài SV chỉ còn DUY NHẤT 1 BCTĐ
// (iteration = 1, bỏ đợt 2), do TRƯỞNG KHOA duyệt. Bảng con
// `student_research_progress_reports` giữ lifecycle riêng
// (draft → submitted → accepted / adjust_request / rejected).
//
// UI: 1 SectionCard — chip trạng thái + 4 ô text.
//
// Props:
//   - detail: research with .progressReports[]
//   - onDone: close modal
//   - readOnly: Trưởng khoa review chỉ xem
function StatusChip({ status }) {
  const colorMap = {
    [PROGRESS_REPORT_STATUS_DRAFT]:          "#9e9e9e",
    [PROGRESS_REPORT_STATUS_SUBMITTED]:      "#1976d2",
    [PROGRESS_REPORT_STATUS_ACCEPTED]:       "#4caf50",
    [PROGRESS_REPORT_STATUS_ADJUST_REQUEST]: "#ff9800",
    [PROGRESS_REPORT_STATUS_REJECTED]:       "#f44336",
  };
  return (
    <Chip
      size="small"
      label={PROGRESS_REPORT_STATUS_LABEL[status] || "Chưa tạo"}
      style={{
        backgroundColor: colorMap[status] || "#bdbdbd",
        color: "#fff",
        height: 22,
        fontSize: 12,
      }}
    />
  );
}

function ProgressReportCard({ report, readOnly, windowOpen, windowReason, onSubmit }) {
  const status = report?.status || PROGRESS_REPORT_STATUS_DRAFT;

  const canSubmit =
    !readOnly &&
    windowOpen &&
    (status === PROGRESS_REPORT_STATUS_DRAFT || status === PROGRESS_REPORT_STATUS_ADJUST_REQUEST);

  const [fields, setFields] = useState({
    completedWork: report?.completedWork || "",
    evaluation: report?.evaluation || "",
    nextPlan: report?.nextPlan || "",
    recommendation: report?.recommendation || "",
  });
  const [saving, setSaving] = useState(false);

  const requiredFilled =
    fields.completedWork.trim() && fields.evaluation.trim() && fields.nextPlan.trim();

  const handleSubmit = async () => {
    if (!requiredFilled) return;
    setSaving(true);
    try {
      await onSubmit(fields);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      icon={<Equalizer />}
      title={
        <Box display="flex" alignItems="center" gridGap={12}>
          <span>Báo cáo tiến độ</span>
          <StatusChip status={status} />
        </Box>
      }
    >
      {report?.reviewComment && (
        <Box marginBottom={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
          <Typography variant="caption" color="textSecondary">
            <b>Nhận xét của Trưởng khoa:</b>
          </Typography>
          <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
            {report.reviewComment}
          </Typography>
        </Box>
      )}
      <Grid container spacing={2}>
        {[
          { label: "1. Công việc đã hoàn thành", key: "completedWork", required: true },
          { label: "2. Đánh giá tiến độ", key: "evaluation", required: true },
          { label: "3. Kế hoạch tiếp theo", key: "nextPlan", required: true },
          { label: "4. Kiến nghị", key: "recommendation", required: false },
        ].map((f) => (
          <Grid item xs={12} key={f.key}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>{f.label}</b>
              {f.required && <span style={{ color: "#c62828" }}> *</span>}
            </Typography>
            <TextField
              fullWidth multiline minRows={3} variant="outlined"
              value={fields[f.key]}
              disabled={readOnly || !canSubmit}
              onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
            />
          </Grid>
        ))}
      </Grid>
      {canSubmit && (
        <Box marginTop={2} display="flex" justifyContent="flex-end">
          <Tooltip title={!windowOpen ? windowReason || "" : ""}>
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Send />}
                onClick={handleSubmit}
                disabled={saving || !requiredFilled || !windowOpen}
              >
                {status === PROGRESS_REPORT_STATUS_ADJUST_REQUEST ? "Gửi lại sau chỉnh sửa" : "Nộp báo cáo tiến độ"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    </SectionCard>
  );
}

export default function ProgressForm({ detail, onDone, readOnly = false }) {
  const dispatch = useDispatch();

  const reports = detail?.progressReports || [];
  // Chỉ còn 1 BCTĐ (iteration = 1). Dữ liệu cũ có thể còn row iteration = 2
  // nhưng không hiển thị nữa.
  const report = useMemo(() => reports.find((r) => r.iteration === 1), [reports]);

  const { check: checkDue } = useDueWindow(detail?.schoolYear);
  const win = checkDue("report1StartTime", "report1EndTime");

  const handleSubmit = async (fields) => {
    const ok = await dispatch(submitProgressReport(detail.id, PROGRESS_REPORT_ITERATION, fields));
    if (ok && onDone) onDone();
  };

  return (
    <Box padding="16px 24px">
      <ProgressReportCard
        report={report}
        readOnly={readOnly}
        windowOpen={win.isOpen}
        windowReason={win.reason}
        onSubmit={handleSubmit}
      />
    </Box>
  );
}
